// Minimal fetch wrapper — used by both web and mobile.
// The server uses Bearer-token auth only; this client injects the
// Authorization header and does NOT send cookies, which avoids
// CORS-credentialed preflight requirements.

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    // Optional, server-defined extra data — used today by 402 quota errors
    // to carry the upgrade context (current tier, required tier, etc.) so
    // clients render the upgrade modal off the same response that blocked
    // them.
    public payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /**
   * Token provider. Web reads from localStorage; mobile from SecureStore.
   * Returns null when no session is active.
   */
  getToken?: () => string | null | Promise<string | null>;
}

export function createApiClient(opts: ApiClientOptions) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token =
      typeof opts.getToken === 'function' ? await Promise.resolve(opts.getToken()) : null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${opts.baseUrl}${path}`, {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
      // Bearer tokens only — never send cookies. 'omit' avoids needing the
      // server to set Access-Control-Allow-Credentials: true.
      credentials: 'omit',
    });

    const contentType = res.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await res.json() : null;

    if (!res.ok) {
      const body = payload as { code?: string; error?: string; payload?: unknown } | null;
      const code = body?.code ?? 'UNKNOWN';
      const message = body?.error ?? res.statusText;
      throw new ApiError(res.status, code, message, body?.payload);
    }

    return payload as T;
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
